import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ClientChartTemplateStatus } from '@/core/models/phase9.models';
import type { ChartAccountRow, ChartOfAccountsOptions } from '@/core/models/trial-balance.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { clientChartTemplateService } from '@/core/services/phase9';
import { trialBalanceService } from '@/core/services/trialBalance';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyState } from '@/kit/EmptyState';
import { FormField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Paginator } from '@/kit/Paginator';
import { Select } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import { openDialog } from '@/kit/dialog';
import { AddNewAccount, type AddNewAccountDialogData } from './AddNewAccount';
import { ApplyFirmTemplateConfirm, type ApplyFirmTemplateConfirmData } from './ApplyFirmTemplateConfirm';
import './TaskTablePage.scss';

const pageSizeOptions = [10, 20, 25, 50, 100];
/** A client's chart runs to hundreds of accounts: 20 to a page by default. */
const DEFAULT_PAGE_SIZE = 20;
const ALL = -1;
const ACTIVE = 1;
const ARCHIVED = 2;
const SEARCH_DEBOUNCE_MS = 400;

const statusOptions = [
  { id: ALL, name: 'All' },
  { id: 1, name: 'Active' },
  { id: 0, name: 'Inactive' },
  { id: ARCHIVED, name: 'Archived' },
];

interface Option {
  id: number;
  name: string;
}

/** Angular TaxreturnChartofaccountsComponent: the client's chart of accounts. */
export function TaxreturnChartofaccounts() {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  const [dataSource, setDataSource] = useState<ChartAccountRow[]>([]);
  /** The first load has finished: only then is an empty list "empty" (not still loading). */
  const [loaded, setLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [options, setOptions] = useState<ChartOfAccountsOptions>({ accountTypes: [], vatCodes: [] });
  const [searchText, setSearchText] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // the chart opens on its Active accounts
  const [statusFilter, setStatusFilter] = useState(ACTIVE);
  const [accountTypeFilter, setAccountTypeFilter] = useState(ALL);
  const [accountTypeOptions, setAccountTypeOptions] = useState<Option[]>([{ id: ALL, name: 'All' }]);
  const [searchFocused, setSearchFocused] = useState(false);
  /** Whether this client's chart can be seeded from the firm's template for its business type. */
  const [templateStatus, setTemplateStatus] = useState<ClientChartTemplateStatus | null>(null);
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
  const applyingRef = useRef(false);

  // The filters as the last change left them: `load()` runs from a timer / right after a change, before a render.
  const filters = useRef({ searchText: '', statusFilter: ACTIVE, accountTypeFilter: ALL });
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** A new load supersedes the one in flight (Angular unsubscribed it). */
  const loadToken = useRef(0);
  const clientRef = useRef(client);

  const query = () => {
    const { searchText: text, statusFilter: status, accountTypeFilter: typeId } = filters.current;
    return {
      search: text?.trim() ? text.trim() : null,
      status: status ?? ALL,
      accountTypeId: typeId === ALL || typeId === null ? null : typeId,
    };
  };

  const load = () => {
    const current = clientRef.current;
    if (!current) {
      return;
    }
    const token = ++loadToken.current;
    trialBalanceService.getAccounts(current.id, query()).then(
      (rows) => {
        if (token !== loadToken.current) return;
        setDataSource(rows);
        setLoaded(true);
        const ids = new Set(rows.map((r) => r.id));
        setSelectedIds((selected) => new Set([...selected].filter((id) => ids.has(id))));
      },
      (error) => {
        if (token !== loadToken.current) return;
        setLoaded(true);
        notifyError(error);
      },
    );
  };

  const loadTemplateStatus = () => {
    const current = clientRef.current;
    if (!current) {
      return;
    }
    clientChartTemplateService.getStatus(current.id).then(
      (status) => setTemplateStatus(status),
      () => setTemplateStatus(null),
    );
  };

  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    trialBalanceService.getAccountOptions(client.id).then(
      (result) => {
        setOptions(result);
        setAccountTypeOptions([
          { id: ALL, name: 'All' },
          ...result.accountTypes.map((t) => ({ id: t.id, name: t.name })),
        ]);
      },
      (error) => notifyError(error),
    );
    load();
    loadTemplateStatus();
    const pendingLoad = loadToken;
    const pendingSearch = searchTimer;
    return () => {
      if (pendingSearch.current) {
        clearTimeout(pendingSearch.current);
        pendingSearch.current = null;
      }
      pendingLoad.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit / ngOnDestroy: once
  }, []);

  /** search$ with debounceTime(400): every keystroke restarts the timer, no distinct check. */
  const onSearchChange = (text: string) => {
    filters.current.searchText = text;
    setSearchText(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      searchTimer.current = null;
      load();
    }, SEARCH_DEBOUNCE_MS);
  };

  /** ap5-a4: the offer stands while the firm template still holds a code this client has not got. */
  const canOfferTemplate = !!templateStatus && templateStatus.canApply;
  const clientName = templateStatus?.clientName ?? client?.name ?? '';

  /**
   * Seeds the client's chart from the firm template, once confirmed (BUG-034); existing account codes are never
   * duplicated.
   */
  const applyFirmTemplate = async () => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (applyingRef.current) {
      return;
    }
    applyingRef.current = true;
    const confirmed = await openDialog<ApplyFirmTemplateConfirmData, boolean>(ApplyFirmTemplateConfirm, {
      data: {
        clientName,
        businessTypeName: templateStatus?.businessTypeName ?? 'client',
        message: templateStatus?.message ?? '',
      },
    }).afterClosed();
    if (confirmed !== true) {
      applyingRef.current = false;
      return;
    }
    setIsApplyingTemplate(true);
    const done = () => {
      applyingRef.current = false;
      setIsApplyingTemplate(false);
    };
    clientChartTemplateService.applyTemplate(client.id).then(
      (result) => {
        done();
        notifySuccess(result.message);
        load();
        loadTemplateStatus();
      },
      (error) => {
        done();
        notifyError(error);
      },
    );
  };

  const isAllSelected = dataSource.length > 0 && dataSource.every((r) => selectedIds.has(r.id));
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const toggleAll = (checked: boolean) => setSelectedIds(checked ? new Set(dataSource.map((r) => r.id)) : new Set());

  const toggleRow = (row: ChartAccountRow, checked: boolean) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.id);
      } else {
        next.delete(row.id);
      }
      return next;
    });

  /** Opens the account dialog; with a row it edits that account. */
  const openSendTrial = (account?: ChartAccountRow) => {
    if (!client) {
      return;
    }
    const data: AddNewAccountDialogData = { clientId: client.id, options, account };
    openDialog(AddNewAccount, { data, width: '580px', panelClass: 'dialog-align-top' })
      .afterClosed()
      .then((saved) => {
        if (saved) {
          notifySuccess(NotificationTextMessage.successMessage);
          load();
          loadTemplateStatus();
        }
      });
  };

  // The ticked accounts when this row is one of them, so the checkboxes drive the action as on every other list.
  const openDeleteClick = (row: ChartAccountRow) =>
    confirmDelete(selectedIds.has(row.id) && selectedIds.size > 1 ? [...selectedIds] : [row.id]);

  const confirmDelete = (ids: string[]) => {
    openDialog(ClosePopup, { data: { itemName: ids.length > 1 ? 'Accounts' : 'Account', count: ids.length } })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        trialBalanceService.deleteAccounts(ids).then(
          () => {
            setSelectedIds((current) => {
              const next = new Set(current);
              ids.forEach((id) => next.delete(id));
              return next;
            });
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load();
            loadTemplateStatus();
          },
          (error) => notifyError(error),
        );
      });
  };

  /** Archives the ticked accounts, or restores them when the Archived accounts are the ones shown. */
  const archiveSelected = () => {
    if (!client || selectedIds.size === 0) return;
    const ids = [...selectedIds];
    const archive = statusFilter !== ARCHIVED;
    openDialog(ClosePopup, {
      data: {
        itemName: ids.length > 1 ? 'Accounts' : 'Account',
        count: ids.length,
        action: archive ? 'Archive' : 'Restore',
      },
    })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) return;
        trialBalanceService.archiveAccounts(ids, archive).then(
          () => {
            notifySuccess(archive ? 'Account(s) archived successfully' : 'Account(s) restored successfully');
            setSelectedIds(new Set());
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  /** format: 1 CSV, 2 Excel, 3 PDF; exports the selected rows, or every row matching the filters. */
  const onExport = (format: number) => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    trialBalanceService
      .exportAccounts(client.id, { ...query(), format, isPrint: false, ids: [...selectedIds] })
      .catch((error) => notifyError(error));
  };

  const columns: DataTableColumn<ChartAccountRow>[] = [
    {
      id: 'accountname',
      header: (
        <>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isSomeSelected}
            onChange={(event) => toggleAll(event.target.checked)}
          />
          Account Name
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={selectedIds.has(element.id)}
            onChange={(event) => toggleRow(element, event.target.checked)}
          />{' '}
          {element.accountName}{' '}
        </>
      ),
    },
    { id: 'category', header: 'Category', cell: (element) => element.category },
    { id: 'group', header: 'Group', cell: (element) => element.group },
    {
      id: 'code',
      header: 'Code',
      cell: (element) =>
        element.isStandard ? (
          <span className="chart-code-standard" title="Standard code">
            {element.code}
          </span>
        ) : (
          element.code
        ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Edit" onClick={() => openSendTrial(element)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => openDeleteClick(element)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  const maxPageIndex = Math.max(0, Math.ceil(dataSource.length / pageSize) - 1);
  const shownPageIndex = Math.min(pageIndex, maxPageIndex);
  const pageRows = dataSource.slice(shownPageIndex * pageSize, shownPageIndex * pageSize + pageSize);

  return (
    <SideMenuPage menu="settings" contentClassName="tr-table-page">
      <div className="content-body">
        {clientName && (
          <div className="content-header mb-0">
            <div className="content-header-left mb-1">
              <h2 className="m-0">{clientName} - Chart of Accounts</h2>
            </div>
          </div>
        )}
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={() => openSendTrial()}>
              <Icon name="add" />
              <span>Account</span>
            </Button>
            {canOfferTemplate && (
              <Button
                className="action-button secondary-button"
                disabled={isApplyingTemplate}
                title={templateStatus?.message}
                onClick={applyFirmTemplate}
              >
                <Icon name="playlist_add" outlined />
                <span>Apply Firm Chart Template</span>
              </Button>
            )}
            {selectedIds.size > 0 && (
              <Button className="action-button secondary-button" onClick={archiveSelected}>
                <Icon name={statusFilter === ARCHIVED ? 'unarchive' : 'archive'} outlined />
                <span>{statusFilter === ARCHIVED ? 'Restore' : 'Archive'}</span>
              </Button>
            )}
            <FormField className="search-field" focused={searchFocused}>
              <Icon name="search" />
              <input
                className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
                placeholder="Search"
                autoComplete="off"
                value={searchText}
                onChange={(event) => {
                  setPageIndex(0);
                  onSearchChange(event.target.value);
                }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
            </FormField>
          </div>

          <div className="content-header-right mb-1">
            <Select<Option>
              className="w-150"
              openOnFocus
              items={statusOptions}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              searchable={false}
              value={statusFilter}
              onChange={(value) => {
                filters.current.statusFilter = value as number;
                setStatusFilter(value as number);
                setPageIndex(0);
                load();
              }}
            />
            <Select<Option>
              className="w-150"
              openOnFocus
              items={accountTypeOptions}
              bindLabel="name"
              bindValue="id"
              clearable={false}
              value={accountTypeFilter}
              onChange={(value) => {
                filters.current.accountTypeFilter = value as number;
                setAccountTypeFilter(value as number);
                setPageIndex(0);
                load();
              }}
            />
            <div className="toolbar">
              <Button title="Export to Excel" onClick={() => onExport(2)}>
                <Icon name="border_all" className="icon-color-xls" />
              </Button>
              <Button title="Export to CSV" onClick={() => onExport(1)}>
                <Icon name="description" outlined className="icon-color-xls" />
              </Button>
              <Button title="Export to PDF" onClick={() => onExport(3)}>
                <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              </Button>
            </div>
          </div>
        </div>

        {canOfferTemplate && <div className="info-message primary-message mb-1">{templateStatus?.message}</div>}
        <SelectionBar
          count={selectedIds.size}
          onDelete={() => confirmDelete([...selectedIds])}
          onExport={() => onExport(2)}
          onClear={() => setSelectedIds(new Set())}
        >
          <Button className="action-button secondary-button" onClick={archiveSelected}>
            <Icon name={statusFilter === ARCHIVED ? 'unarchive' : 'archive'} outlined />
            <span>{statusFilter === ARCHIVED ? 'Restore' : 'Archive'}</span>
          </Button>
        </SelectionBar>

        {/* The paginated height (TaskTablePage.scss) leaves the paginator on screen below the grid. */}
        <div className="mat-table-wrapper mat-table-wrapper-pagination">
          <DataTable
            columns={columns}
            rows={pageRows}
            rowKey={(row) => row.id}
            stickyHeader
            noDataMessage={
              loaded ? (
                searchText.trim() || accountTypeFilter !== ALL ? (
                  <EmptyState
                    icon="search_off"
                    title="No accounts match"
                    hint="Clear the search or change the filters to see more accounts."
                  />
                ) : (
                  <EmptyState
                    icon="list_alt"
                    title="No accounts yet"
                    hint="Add an account to build this client's chart of accounts."
                    action={
                      <Button className="action-button primary-button" onClick={() => openSendTrial()}>
                        <Icon name="add" />
                        <span>Add the first account</span>
                      </Button>
                    }
                  />
                )
              ) : undefined
            }
          />
        </div>
        <Paginator
          length={dataSource.length}
          pageIndex={shownPageIndex}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          showFirstLastButtons
          onPage={(event) => {
            setPageIndex(event.pageIndex);
            setPageSize(event.pageSize);
          }}
        />
      </div>
    </SideMenuPage>
  );
}
